(() => {
  const ROOT = '[data-d2-app]';
  const ID_STORAGE = 'die2-voranmeldung-submission-id-v2';
  const LEGACY_ID_STORAGE = 'die2-voranmeldung-submission-id';
  const ID_TTL = 12 * 60 * 60 * 1000;

  class Die2Voranmeldung {
    constructor(root) {
      this.root = root;
      this.startScreen = root.querySelector('[data-d2-start]');
      this.flow = root.querySelector('[data-d2-flow]');
      this.form = root.querySelector('[data-d2-form]');
      this.complete = root.querySelector('[data-d2-complete]');
      this.steps = [...root.querySelectorAll('[data-d2-step]')];
      this.next = root.querySelector('[data-d2-next]');
      this.back = root.querySelector('[data-d2-back]');
      this.currentStep = 1;
      this.submitting = false;
      this.config = this.readConfig();
      this.routes = this.buildRoutes();
      this.submissionId = this.restoreSubmissionId();
      if (!this.startScreen || !this.flow || !this.form || !this.next) return;
      this.bind();
      this.configureDates();
      this.hydrateVideoSlots();
    }

    readConfig() {
      try {
        const config = JSON.parse(this.root.querySelector('[data-d2-config]')?.textContent || '{}');
        Object.values(config.priceOverrides || {}).forEach((profile) => {
          profile.external = String(profile.externalText || '')
            .split(/\r?\n/)
            .map((entry) => entry.trim())
            .filter(Boolean);
          delete profile.externalText;
        });
        return config;
      } catch (_) {
        return { submission: {}, links: {}, icons: {}, products: {}, prices: {} };
      }
    }

    buildRoutes() {
      return {
        b_schalter: {
          label: 'Klasse B – Schaltung', category: 'PKW', product: 'b_schalter', price: 'b_standard', checklist: 'standard',
          intro: 'Du absolvierst Ausbildung und Prüfung auf einem Schaltfahrzeug und darfst danach Schalt- und Automatikfahrzeuge fahren.',
          facts: ['Mindestalter 18 Jahre, mit BF17 ab 17 Jahren', 'Theorie- und Praxisprüfung', 'Ausbildung und Prüfung auf einem Schaltfahrzeug']
        },
        b_automatik: {
          label: 'Klasse B – Automatik', category: 'PKW', product: 'b_automatik', price: 'b_standard', checklist: 'standard',
          intro: 'Du absolvierst Ausbildung und Prüfung auf einem Automatikfahrzeug. Danach darfst du ausschließlich Automatikfahrzeuge fahren.',
          facts: ['Mindestalter 18 Jahre, mit BF17 ab 17 Jahren', 'Theorie- und Praxisprüfung', 'Schlüsselzahl 78 beschränkt auf Automatik']
        },
        b197: {
          label: 'Klasse B197', category: 'PKW', product: 'b197', price: 'b_standard', checklist: 'standard',
          intro: 'Du lernst auf Schalt- und Automatikfahrzeugen und legst die praktische Prüfung auf einem Automatikfahrzeug ab.',
          facts: ['Mindestalter 18 Jahre, mit BF17 ab 17 Jahren', 'Mindestens zehn Schaltstunden zu je 45 Minuten', '15-minütige Testfahrt in der Fahrschule', 'Keine Automatikbeschränkung nach bestandener Prüfung']
        },
        b96: {
          label: 'Klasse B96', category: 'PKW', product: 'b96', price: 'b96', checklist: 'b96', payInFull: true,
          prerequisite: 'Ich besitze bereits die Fahrerlaubnis Klasse B.',
          intro: 'B96 erweitert Klasse B für größere Pkw-Anhänger-Kombinationen. Eine zusätzliche Prüfung ist nicht erforderlich.',
          facts: ['Mindestens siebenstündige Fahrerschulung', 'Keine Theorie- oder Praxisprüfung', 'Zulässige Gesamtmasse der Kombination bis 4.250 kg']
        },
        be: {
          label: 'Klasse BE', category: 'PKW', product: 'be', price: 'be', checklist: 'be',
          prerequisite: 'Ich besitze bereits die Fahrerlaubnis Klasse B.',
          intro: 'Klasse BE ist die eigenständige Anhänger-Fahrerlaubnis mit praktischer Prüfung.',
          facts: ['Vorbesitz Klasse B erforderlich', 'Keine Theorieprüfung', 'Praktische Ausbildung und Prüfung mit Anhänger']
        },
        a_standard: {
          label: 'Klasse A – regulärer Direkteinstieg', category: 'Motorrad', product: 'a_standard', price: 'a_standard', checklist: 'standard',
          intro: 'Mit Klasse A darfst du Motorräder ohne Leistungsbeschränkung fahren.',
          facts: ['Direkteinstieg grundsätzlich ab 24 Jahren', 'Theorie- und Praxisprüfung', 'Keine Leistungsbeschränkung']
        },
        a_stage: {
          label: 'Stufenaufstieg A2 → A', category: 'Motorrad', product: 'a_stage', price: 'stage', checklist: 'stage',
          prerequisite: 'Ich besitze die Fahrerlaubnis Klasse A2 seit mindestens zwei Jahren.',
          intro: 'Beim Stufenaufstieg von A2 auf A ist keine Theorieprüfung und keine gesetzliche Mindestzahl an Fahrstunden vorgeschrieben.',
          facts: ['A2 seit mindestens zwei Jahren', 'Keine Theorieprüfung', 'Praktische Prüfung erforderlich']
        },
        a2_standard: {
          label: 'Klasse A2 – regulärer Direkteinstieg', category: 'Motorrad', product: 'a2_standard', price: 'a_standard', checklist: 'standard',
          intro: 'Mit Klasse A2 darfst du Motorräder bis 35 kW fahren.',
          facts: ['Mindestalter 18 Jahre', 'Theorie- und Praxisprüfung', 'Motorräder bis 35 kW']
        },
        a2_stage: {
          label: 'Stufenaufstieg A1 → A2', category: 'Motorrad', product: 'a2_stage', price: 'stage', checklist: 'stage',
          prerequisite: 'Ich besitze die Fahrerlaubnis Klasse A1 seit mindestens zwei Jahren.',
          intro: 'Beim Stufenaufstieg von A1 auf A2 ist keine Theorieprüfung und keine gesetzliche Mindestzahl an Fahrstunden vorgeschrieben.',
          facts: ['A1 seit mindestens zwei Jahren', 'Keine Theorieprüfung', 'Praktische Prüfung erforderlich']
        },
        a1: {
          label: 'Klasse A1', category: 'Motorrad', product: 'a1', price: 'a1', checklist: 'standard',
          intro: 'Klasse A1 ist dein Einstieg in die Welt der 125er-Motorräder.',
          facts: ['Mindestalter 16 Jahre', 'Motorräder bis 125 cm³ und 11 kW', 'Theorie- und Praxisprüfung']
        },
        b196: {
          label: 'Schlüsselzahl B196', category: 'Motorrad', product: 'b196', price: 'b196', checklist: 'b196',
          prerequisite: 'Ich bin mindestens 25 Jahre alt und besitze Klasse B seit mindestens fünf Jahren.',
          intro: 'Mit B196 darfst du in Deutschland Leichtkrafträder der Klasse A1 fahren – ohne zusätzliche Prüfung.',
          facts: ['Mindestalter 25 Jahre', 'Klasse B seit mindestens fünf Jahren', 'Vier Theorieeinheiten und mindestens fünf praktische Einheiten zu je 90 Minuten', 'Gilt nur in Deutschland']
        },
        am: {
          label: 'Klasse AM', category: 'Motorrad', product: 'am', price: 'am', checklist: 'standard',
          intro: 'Klasse AM ist der Einstieg für Roller, Mopeds und leichte Fahrzeuge bis 45 km/h.',
          facts: ['Mindestalter 15 Jahre', 'Theorie- und Praxisprüfung', 'Keine gesetzlich vorgeschriebenen Sonderfahrten']
        },
        mofa: {
          label: 'Mofa-Prüfbescheinigung', category: 'Motorrad', product: 'mofa', price: 'mofa', checklist: 'mofa',
          intro: 'Mit der Mofa-Prüfbescheinigung darfst du einsitzige Mofas bis 25 km/h fahren.',
          facts: ['Mindestalter 15 Jahre', 'Theoretische Prüfung', 'Theorie- und Praxisausbildung in der Fahrschule']
        }
      };
    }

    bind() {
      this.root.querySelector('[data-d2-start-button]')?.addEventListener('click', () => this.start());
      this.next.addEventListener('click', () => this.goNext());
      this.back?.addEventListener('click', () => this.goBack());

      this.form.addEventListener('change', (event) => {
        const target = event.target;
        if (target.name === 'd2-category') this.syncCategory();
        if (target.name === 'd2-pkw-route') this.syncPkwOptions();
        if (target.name === 'd2-moto-class') this.syncMotorcycleOptions();
        if (target.name === 'd2-moto-training') this.syncPrerequisite();
        if (target.name === 'd2-has-license') this.toggleLicenseClasses();
        if (target.matches('input[type="radio"], input[type="checkbox"]')) this.syncChoiceStyles();
        this.clearStatuses();
        this.updateNavigation();
      });

      this.root.querySelectorAll('[data-d2-video-play]').forEach((button) => {
        button.addEventListener('click', () => this.playVideo(button.closest('[data-d2-video-shell]')));
      });
    }

    configureDates() {
      const birthDate = this.field('birthDate');
      if (birthDate) birthDate.max = new Date().toISOString().slice(0, 10);
    }

    start() {
      this.startScreen.hidden = true;
      this.flow.hidden = false;
      this.complete.hidden = true;
      this.form.hidden = false;
      this.showStep(1, false);
    }

    showStep(number, focus = true) {
      const target = this.steps.find((step) => Number(step.dataset.d2Step) === number);
      if (!target) return;
      this.pauseVideos();
      this.currentStep = number;
      this.steps.forEach((step) => {
        const active = step === target;
        step.hidden = !active;
        step.classList.toggle('is-active', active);
      });
      if (number === 2) this.syncCategory();
      if (number >= 3) this.renderRoute();
      if (number === 7) this.renderSummary();
      if (number === 8) this.renderFinal();
      this.setStepVideo(number);
      this.updateProgress();
      this.updateNavigation();
      this.autoplayVideo(target);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      if (focus) window.setTimeout(() => target.querySelector('h2')?.focus({ preventScroll: true }), 250);
    }

    updateProgress() {
      const label = this.root.querySelector('[data-d2-step-label]');
      if (label) label.textContent = `Schritt ${this.currentStep}`;
      this.root.querySelectorAll('[data-d2-progress-dot]').forEach((dot) => {
        const step = Number(dot.dataset.step);
        dot.classList.toggle('is-current', step === this.currentStep);
        dot.classList.toggle('is-complete', step < this.currentStep);
      });
    }

    updateNavigation() {
      if (!this.next) return;
      this.next.disabled = this.submitting;
      if (this.currentStep === 8) this.next.textContent = this.isMinor() ? 'Daten absenden' : 'Zur Zahlung';
      else if (this.currentStep === 7) this.next.textContent = 'Angaben bestätigen';
      else this.next.textContent = 'Weiter';
    }

    goBack() {
      if (this.submitting) return;
      if (this.currentStep <= 1) {
        this.pauseVideos();
        this.flow.hidden = true;
        this.startScreen.hidden = false;
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      this.showStep(this.currentStep - 1);
    }

    goNext() {
      if (this.submitting) return;
      if (this.currentStep === 1 && !this.selectedCategory()) {
        this.showStatus(1, 'Bitte wähle zuerst PKW oder Motorrad aus.');
        return;
      }
      if (this.currentStep === 2 && !this.validateRouteSelection()) return;
      if (this.currentStep === 6 && !this.validatePersonalData()) return;
      if (this.currentStep === 8) {
        this.submit();
        return;
      }
      this.showStep(this.currentStep + 1);
    }

    selectedCategory() {
      return this.form.querySelector('input[name="d2-category"]:checked')?.value || '';
    }

    currentRouteKey() {
      const category = this.selectedCategory();
      if (category === 'pkw') return this.form.querySelector('input[name="d2-pkw-route"]:checked')?.value || null;
      if (category !== 'motorrad') return null;
      const motorcycleClass = this.form.querySelector('input[name="d2-moto-class"]:checked')?.value;
      if (!motorcycleClass) return null;
      if (motorcycleClass === 'a' || motorcycleClass === 'a2') {
        const training = this.form.querySelector('input[name="d2-moto-training"]:checked')?.value;
        return training ? `${motorcycleClass}_${training}` : null;
      }
      return motorcycleClass;
    }

    currentRoute() {
      const key = this.currentRouteKey();
      const base = this.routes[key];
      if (!base) return null;
      const bf17 = ['b_schalter', 'b_automatik', 'b197'].includes(key) && Boolean(this.root.querySelector('[data-d2-bf17]')?.checked);
      return { ...base, key, bf17 };
    }

    priceForRoute(route = this.currentRoute()) {
      if (!route) return {};
      return this.config.priceOverrides?.[route.key] || this.config.prices?.[route.price] || {};
    }

    syncCategory() {
      const category = this.selectedCategory();
      this.root.querySelectorAll('[data-d2-route-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.d2RoutePanel !== category;
      });
      this.root.querySelectorAll('[data-d2-vehicle-image]').forEach((image) => {
        image.hidden = image.dataset.d2VehicleImage !== (category || 'pkw');
      });
      this.syncChoiceStyles();
      if (category === 'pkw') this.syncPkwOptions();
      if (category === 'motorrad') this.syncMotorcycleOptions();
      this.syncPrerequisite();
    }

    syncPkwOptions() {
      const key = this.form.querySelector('input[name="d2-pkw-route"]:checked')?.value;
      const bf17Row = this.root.querySelector('[data-d2-bf17-row]');
      const bf17 = this.root.querySelector('[data-d2-bf17]');
      const allowed = ['b_schalter', 'b_automatik', 'b197'].includes(key);
      if (bf17Row) bf17Row.hidden = !allowed;
      if (!allowed && bf17) bf17.checked = false;
      this.syncPrerequisite();
    }

    syncMotorcycleOptions() {
      const selected = this.form.querySelector('input[name="d2-moto-class"]:checked')?.value;
      const training = this.root.querySelector('[data-d2-training]');
      const needsTraining = selected === 'a' || selected === 'a2';
      if (training) training.hidden = !needsTraining;
      if (!needsTraining) {
        this.form.querySelectorAll('input[name="d2-moto-training"]').forEach((input) => { input.checked = false; });
      }
      const label = this.root.querySelector('[data-d2-stage-label]');
      if (label) label.textContent = selected === 'a' ? 'A2 → A' : 'A1 → A2';
      this.syncPrerequisite();
      this.syncChoiceStyles();
    }

    syncPrerequisite() {
      const row = this.root.querySelector('[data-d2-prerequisite-row]');
      const input = this.root.querySelector('[data-d2-prerequisite]');
      const copy = this.root.querySelector('[data-d2-prerequisite-copy]');
      if (!row || !input || !copy) return;
      const key = this.currentRouteKey();
      const requirement = this.routes[key]?.prerequisite || '';
      if (input.dataset.route !== String(key || '')) input.checked = false;
      input.dataset.route = String(key || '');
      row.hidden = !requirement;
      copy.textContent = requirement;
      if (!requirement) input.checked = false;
    }

    syncChoiceStyles() {
      this.root.querySelectorAll('.d2-choice').forEach((choice) => {
        choice.classList.toggle('is-selected', Boolean(choice.querySelector('input:checked')));
      });
    }

    validateRouteSelection() {
      const route = this.currentRoute();
      if (!route) {
        this.showStatus(2, 'Bitte wähle zuerst eine Ausbildung und gegebenenfalls den Ausbildungsweg aus.');
        return false;
      }
      if (route.prerequisite && !this.root.querySelector('[data-d2-prerequisite]')?.checked) {
        this.showStatus(2, 'Bitte bestätige zuerst die Voraussetzung für diese Ausbildung.');
        return false;
      }
      return true;
    }

    renderRoute() {
      const route = this.currentRoute();
      if (!route) return;
      const title = this.root.querySelector('[data-d2-detail-title]');
      const intro = this.root.querySelector('[data-d2-detail-intro]');
      const card = this.root.querySelector('[data-d2-detail-card]');
      if (title) title.textContent = route.label;
      if (intro) intro.textContent = route.intro;
      if (card) {
        card.replaceChildren();
        const heading = document.createElement('h3');
        heading.textContent = 'Das Wichtigste auf einen Blick';
        const list = document.createElement('ul');
        route.facts.forEach((fact) => {
          const item = document.createElement('li');
          item.textContent = fact;
          list.append(item);
        });
        if (route.bf17) {
          const item = document.createElement('li');
          item.textContent = 'Du hast BF17 als Zusatzoption ausgewählt.';
          list.append(item);
        }
        card.append(heading, list);
      }
      this.renderPrices(route);
      this.renderChecklist(route);
    }

    renderPrices(route) {
      const price = this.priceForRoute(route);
      const list = this.root.querySelector('[data-d2-price-list]');
      if (list) {
        list.replaceChildren();
        (price.rows || []).forEach((row) => {
          const wrapper = document.createElement('div');
          const term = document.createElement('dt');
          const value = document.createElement('dd');
          term.textContent = row.label;
          value.textContent = row.value;
          wrapper.append(term, value);
          if (row.note) {
            const note = document.createElement('small');
            note.textContent = row.note;
            wrapper.append(note);
          }
          list.append(wrapper);
        });
      }
      const total = this.root.querySelector('[data-d2-total]');
      if (total) total.hidden = !price.total;
      const totalValue = this.root.querySelector('[data-d2-total-value]');
      const totalNote = this.root.querySelector('[data-d2-total-note]');
      if (totalValue) totalValue.textContent = price.total || '';
      if (totalNote) totalNote.textContent = price.totalNote || '';
      const external = this.root.querySelector('[data-d2-external-list]');
      if (external) {
        external.replaceChildren();
        (price.external || []).forEach((entry) => {
          const item = document.createElement('li');
          item.textContent = entry;
          external.append(item);
        });
      }
      this.root.querySelectorAll('[data-d2-due], [data-d2-final-due], [data-d2-final-due-minor]').forEach((node) => {
        node.textContent = price.due || price.base || '–';
      });
      const dueCopy = this.root.querySelector('[data-d2-due-copy]');
      if (dueCopy) {
        dueCopy.textContent = route.payInFull
          ? 'Für Volljährige wird dieser vollständige Schulungsbetrag im Checkout bezahlt.'
          : 'Für Volljährige wird dieser Grundbetrag im Checkout als vollständig anrechenbare Vorauszahlung bezahlt.';
      }
    }

    renderChecklist(route) {
      const definitions = {
        passbild: ['Biometrisches Passbild', this.config.icons?.passbild],
        antrag: ['Fahrerlaubnis-\nantrag', this.config.icons?.antrag],
        ersteHilfe: ['Erste-Hilfe-Nachweis', this.config.icons?.ersteHilfe],
        sehtest: ['Sehtest-Bescheinigung', this.config.icons?.sehtest],
        bFuehrerschein: ['Führerschein Klasse B', ''],
        vorbesitz: ['Nachweis der vorhandenen Fahrerlaubnis', ''],
        ausweis: ['Personalausweis oder Reisepass', '']
      };
      const sets = {
        standard: ['passbild', 'antrag', 'ersteHilfe', 'sehtest'],
        b96: ['ausweis', 'bFuehrerschein', 'passbild', 'antrag'],
        be: ['ausweis', 'bFuehrerschein', 'passbild', 'antrag', 'ersteHilfe', 'sehtest'],
        stage: ['ausweis', 'vorbesitz', 'passbild', 'antrag'],
        b196: ['ausweis', 'bFuehrerschein', 'passbild', 'antrag'],
        mofa: ['ausweis', 'passbild']
      };
      const list = this.root.querySelector('[data-d2-checklist]');
      if (!list) return;
      list.replaceChildren();
      (sets[route.checklist] || sets.standard).forEach((key) => {
        const [label, icon] = definitions[key];
        const item = document.createElement('div');
        item.className = `d2-check-item d2-check-item--${key}`;
        if (icon) {
          const image = document.createElement('img');
          image.src = icon;
          image.alt = '';
          item.append(image);
        } else {
          const marker = document.createElement('span');
          marker.className = 'd2-check-item__marker';
          marker.setAttribute('aria-hidden', 'true');
          item.append(marker);
        }
        const text = document.createElement('strong');
        text.textContent = label;
        item.append(text);
        list.append(item);
      });
    }

    toggleLicenseClasses() {
      const yes = this.form.querySelector('input[name="d2-has-license"]:checked')?.value === 'yes';
      const wrapper = this.root.querySelector('[data-d2-license-classes]');
      if (wrapper) wrapper.hidden = !yes;
      if (!yes) wrapper?.querySelectorAll('input').forEach((input) => { input.checked = false; });
    }

    validatePersonalData() {
      const step = this.steps.find((item) => Number(item.dataset.d2Step) === 6);
      const fields = [...step.querySelectorAll('input[required], select[required], textarea[required]')];
      for (const field of fields) {
        if (!field.checkValidity()) {
          field.reportValidity();
          field.focus();
          return false;
        }
      }
      const ageMessage = this.agePlausibilityMessage();
      if (ageMessage) {
        this.showStatus(6, ageMessage);
        return false;
      }
      return true;
    }

    calculateAge(dateString) {
      if (!dateString) return null;
      const birth = new Date(`${dateString}T12:00:00`);
      if (Number.isNaN(birth.getTime())) return null;
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      const beforeBirthday = today.getMonth() < birth.getMonth() || (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate());
      if (beforeBirthday) age -= 1;
      return age;
    }

    ageInMonths(dateString) {
      const birth = new Date(`${dateString}T12:00:00`);
      const today = new Date();
      if (Number.isNaN(birth.getTime())) return null;
      let months = (today.getFullYear() - birth.getFullYear()) * 12 + today.getMonth() - birth.getMonth();
      if (today.getDate() < birth.getDate()) months -= 1;
      return months;
    }

    agePlausibilityMessage() {
      const route = this.currentRoute();
      const birthDate = this.value('birthDate');
      const age = this.calculateAge(birthDate);
      const months = this.ageInMonths(birthDate);
      if (!route || age === null || months === null) return 'Bitte prüfe dein Geburtsdatum.';
      const earliestMonths = {
        b_schalter: route.bf17 ? 16.5 * 12 : 17.5 * 12,
        b_automatik: route.bf17 ? 16.5 * 12 : 17.5 * 12,
        b197: route.bf17 ? 16.5 * 12 : 17.5 * 12,
        b96: 17.5 * 12,
        be: 17.5 * 12,
        a_standard: 23.5 * 12,
        a_stage: 19.5 * 12,
        a2_standard: 17.5 * 12,
        a2_stage: 17.5 * 12,
        a1: 15.5 * 12,
        am: 14.5 * 12,
        mofa: 14.5 * 12,
        b196: 25 * 12
      };
      if (months < earliestMonths[route.key]) return `Für ${route.label} ist eine Voranmeldung mit diesem Geburtsdatum noch nicht möglich.`;
      if (route.bf17 && age >= 18) return 'BF17 ist für dich nicht mehr erforderlich. Bitte entferne die BF17-Option.';
      return '';
    }

    isMinor() {
      const age = this.calculateAge(this.value('birthDate'));
      return age !== null && age < 18;
    }

    renderSummary() {
      const route = this.currentRoute();
      if (!route) return;
      this.renderDefinitionList(this.root.querySelector('[data-d2-summary-selection]'), [
        ['Bereich', route.category],
        ['Ausbildung', route.label],
        ['BF17', route.bf17 ? 'Ja' : 'Nein'],
        ['Heute fällig', this.priceForRoute(route).due || '–'],
        ['Zahlungsweg', this.isMinor() ? 'Keine Onlinezahlung' : 'Shopify-Checkout']
      ]);
      const licenseAnswer = this.form.querySelector('input[name="d2-has-license"]:checked')?.value;
      const licenses = [...this.root.querySelectorAll('[data-d2-license-class]:checked')].map((input) => input.value).join(', ');
      this.renderDefinitionList(this.root.querySelector('[data-d2-summary-personal]'), [
        ['Name', `${this.value('firstName')} ${this.value('lastName')}`],
        ['Geburtsdatum', this.formatDate(this.value('birthDate'))],
        ['Geburtsort', this.value('birthPlace')],
        ['Anschrift', `${this.value('street')}, ${this.value('zip')} ${this.value('city')}`],
        ['E-Mail', this.value('email')],
        ['Telefon', this.value('phone')],
        ['Vorhandene Fahrerlaubnis', licenseAnswer === 'yes' ? (licenses || 'Ja, keine Klasse angegeben') : 'Nein'],
        ['Hinweise', this.value('notes') || '–']
      ]);
    }

    renderDefinitionList(list, entries) {
      if (!list) return;
      list.replaceChildren();
      entries.forEach(([label, value]) => {
        const wrapper = document.createElement('div');
        const term = document.createElement('dt');
        const description = document.createElement('dd');
        term.textContent = label;
        description.textContent = value;
        wrapper.append(term, description);
        list.append(wrapper);
      });
    }

    renderFinal() {
      const route = this.currentRoute();
      if (!route) return;
      const minor = this.isMinor();
      const adultCard = this.root.querySelector('[data-d2-adult-final]');
      const minorCard = this.root.querySelector('[data-d2-minor-final]');
      const copy = this.root.querySelector('[data-d2-final-copy]');
      if (adultCard) adultCard.hidden = minor;
      if (minorCard) minorCard.hidden = !minor;
      if (copy) copy.textContent = minor
        ? 'Prüfe deine Angaben und sende deine Voranmeldung ohne Onlinezahlung ab.'
        : 'Prüfe den heute fälligen Grundbetrag und starte anschließend den sicheren Shopify-Checkout.';
      this.renderPrices(route);
      this.setVideoSlot('main', minor ? 'final_minor' : 'final_adult', this.steps.find((item) => Number(item.dataset.d2Step) === 8));
    }

    requiredConsentsAccepted() {
      const keys = this.isMinor() ? ['accuracyMinor', 'minorTerms'] : ['accuracy', 'adultTerms'];
      return keys.every((key) => this.root.querySelector(`[data-d2-consent="${key}"]`)?.checked);
    }

    consentSnapshot() {
      const minor = this.isMinor();
      const keys = minor ? ['accuracyMinor', 'minorTerms'] : ['accuracy', 'adultTerms'];
      return {
        textVersion: 'die2-voranmeldung-2026-08-13-v2',
        acceptedAt: new Date().toISOString(),
        accepted: Object.fromEntries(keys.map((key) => [key, Boolean(this.root.querySelector(`[data-d2-consent="${key}"]`)?.checked)]))
      };
    }

    submissionData(status) {
      const route = this.currentRoute();
      const price = this.priceForRoute(route);
      const product = this.config.products?.[route.product] || {};
      const dueAmount = this.comparablePrice(price.due || price.base || '');
      const dueCents = dueAmount === null ? null : Math.round(dueAmount * 100);
      const selectedLicenses = [...this.root.querySelectorAll('[data-d2-license-class]:checked')].map((input) => input.value);
      const data = {
        schemaVersion: 3,
        eventType: 'submission.created',
        submissionId: this.submissionId,
        revision: 1,
        status,
        createdAt: new Date().toISOString(),
        source: this.config.submission?.source || 'Fahrschule Die2 Voranmeldung',
        origin: window.location.origin,
        honeypot: '',
        customer: {
          firstName: this.value('firstName'), lastName: this.value('lastName'), birthDate: this.value('birthDate'),
          birthPlace: this.value('birthPlace'), email: this.value('email'), phone: this.value('phone'), minor: this.isMinor(),
          address: { street: this.value('street'), zip: this.value('zip'), city: this.value('city') }
        },
        selection: {
          routeKey: route.key, category: route.category, label: route.label, bf17: route.bf17,
          prerequisiteConfirmed: Boolean(this.root.querySelector('[data-d2-prerequisite]')?.checked),
          existingLicenses: selectedLicenses,
          notes: this.value('notes')
        },
        payment: {
          onlinePaymentRequired: !this.isMinor(),
          mode: this.isMinor() ? 'keine Onlinezahlung – Vertragsabschluss vor Ort' : 'anrechenbare Vorauszahlung',
          status: this.isMinor() ? 'nicht erforderlich' : 'Checkout gestartet'
        },
        priceSnapshot: {
          capturedAt: new Date().toISOString(), profileKey: route.key, due: price.due || price.base || '', dueCents,
          estimatedTotal: price.total || '', totalNote: price.totalNote || '', rows: price.rows || [], external: price.external || [],
          shopify: { productHandle: product.handle || '', productId: product.productId || 0, variantId: product.variantId || 0, price: product.price || '' }
        },
        consent: this.consentSnapshot()
      };
      data.officeMail = this.buildOfficeMail(data);
      return data;
    }

    buildOfficeMail(data) {
      const subject = `Neue Die2-Voranmeldung · ${data.selection.label} · ${data.customer.firstName} ${data.customer.lastName}`;
      const text = [
        'NEUE VORANMELDUNG', `Vorgangs-ID: ${data.submissionId}`, `Status: ${data.status}`, '',
        'AUSWAHL', `Ausbildung: ${data.selection.label}`, `BF17: ${data.selection.bf17 ? 'Ja' : 'Nein'}`, '',
        'PERSON', `Name: ${data.customer.firstName} ${data.customer.lastName}`, `Geburtsdatum: ${data.customer.birthDate}`,
        `Geburtsort: ${data.customer.birthPlace}`, `Anschrift: ${data.customer.address.street}, ${data.customer.address.zip} ${data.customer.address.city}`,
        `E-Mail: ${data.customer.email}`, `Telefon: ${data.customer.phone}`, `Minderjährig: ${data.customer.minor ? 'Ja' : 'Nein'}`, '',
        'PREIS', `Heute fällig: ${data.priceSnapshot.due}`, `Voraussichtliche Fahrschulkosten: ${data.priceSnapshot.estimatedTotal || 'nicht fest berechenbar'}`, '',
        'ZAHLUNGSWEG', data.payment.mode, '', data.selection.notes ? `HINWEISE\n${data.selection.notes}` : ''
      ].join('\n');
      return { subject, text };
    }

    async submit() {
      const statusNode = this.statusNode(8);
      if (this.root.querySelector('[data-d2-honeypot]')?.value) return;
      const ageMessage = this.agePlausibilityMessage();
      if (ageMessage) {
        this.showStatus(8, ageMessage);
        return;
      }
      if (!this.requiredConsentsAccepted()) {
        this.showStatus(8, 'Bitte bestätige beide Pflichtangaben, bevor du fortfährst.');
        return;
      }
      const webhookUrl = String(this.config.submission?.webhookUrl || '').trim();
      if (!/^https:\/\//i.test(webhookUrl)) {
        this.showStatus(8, this.setupMessage(
          'Die Übermittlung ist noch nicht eingerichtet. Bitte hinterlege den Die2-Make-Webhook im Theme-Editor.',
          'Die Daten können gerade nicht sicher übermittelt werden. Bitte melde dich kurz bei uns.'
        ));
        return;
      }

      this.submitting = true;
      this.setSubmittingLabel('Daten werden gesendet …');
      try {
        const data = this.submissionData(this.isMinor() ? 'voranmeldung_eingegangen' : 'checkout_gestartet');
        if (this.isMinor()) {
          await this.sendWebhook(data);
          this.showCompletion();
          this.resetSubmissionId();
        } else {
          await this.startCheckout(data);
        }
      } catch (error) {
        this.submitting = false;
        this.setSubmittingLabel('');
        if (statusNode) {
          statusNode.textContent = error?.message || 'Das hat leider nicht geklappt. Bitte versuche es noch einmal.';
          statusNode.hidden = false;
        }
      }
    }

    async startCheckout(data) {
      const route = this.currentRoute();
      const product = this.config.products?.[route.product] || {};
      const variantId = Number(product.variantId || 0);
      if (product.available !== true || !Number.isFinite(variantId) || variantId <= 0) {
        throw new Error(this.setupMessage(
          `Das Shopify-Produkt für ${route.label} ist im Theme-Editor noch nicht verfügbar verknüpft.`,
          'Die Onlinezahlung kann gerade nicht vorbereitet werden. Bitte melde dich kurz bei uns.'
        ));
      }
      const expected = this.comparablePrice(this.priceForRoute(route).due);
      const actual = this.comparablePrice(product.price);
      if (expected !== null && actual !== null && Math.abs(expected - actual) > 0.001) {
        throw new Error(this.setupMessage(
          `Der heute fällige Betrag (${this.priceForRoute(route).due}) stimmt nicht mit dem Shopify-Produktpreis (${product.price}) überein.`,
          'Die Onlinezahlung kann gerade nicht sicher vorbereitet werden. Bitte melde dich kurz bei uns.'
        ));
      }
      const shopRoot = window.Shopify?.routes?.root || '/';
      const checkoutUrl = this.buildCheckoutPermalink(shopRoot, variantId, data);
      this.setSubmittingLabel('Daten werden gesendet …');
      await this.sendWebhook(data);
      // Der Checkout-Link enthält weiterhin die bisherige Vorgangs-ID. Für
      // einen späteren neuen Vorgang im selben Tab wird sofort eine neue ID
      // bereitgestellt – auch dann, wenn der Browser diese Seite aus dem
      // Back-Forward-Cache wiederherstellt.
      this.resetSubmissionId();
      this.setSubmittingLabel('Checkout wird geöffnet …');
      window.location.assign(checkoutUrl);
    }

    comparablePrice(value) {
      const normalized = String(value || '').replace(/\s/g, '').replace(/[^\d,.-]/g, '').replace(/\.(?=\d{3}(?:\D|$))/g, '').replace(',', '.');
      const parsed = Number.parseFloat(normalized);
      return Number.isFinite(parsed) ? parsed : null;
    }

    base64UrlEncode(value) {
      const bytes = new TextEncoder().encode(String(value));
      let binary = '';
      bytes.forEach((byte) => {
        binary += String.fromCharCode(byte);
      });
      return window.btoa(binary)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/g, '');
    }

    buildCheckoutPermalink(shopRoot, variantId, data) {
      const normalizedRoot = String(shopRoot || '/').endsWith('/')
        ? String(shopRoot || '/')
        : `${shopRoot}/`;
      const checkoutUrl = new URL(
        `${normalizedRoot}cart/${variantId}:1`,
        window.location.origin
      );
      const properties = this.base64UrlEncode(JSON.stringify({
        '_Die2-Voranmeldung': 'Ja',
        '_Vorgangs-ID': data.submissionId
      }));
      checkoutUrl.searchParams.set('properties', properties);
      return checkoutUrl.toString();
    }

    async sendWebhook(data) {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(this.config.submission.webhookUrl, {
          method: 'POST', credentials: 'omit', signal: controller.signal,
          headers: { 'Content-Type': 'application/json', Accept: 'application/json, text/plain, */*' }, body: JSON.stringify(data)
        });
        if (!response.ok) throw new Error('Die Datenübermittlung wurde abgelehnt. Bitte versuche es später noch einmal.');
        const text = await response.text();
        let result;
        try { result = JSON.parse(text); } catch (_) { throw new Error('Die Datenübermittlung wurde nicht eindeutig bestätigt.'); }
        if (result?.ok !== true || result?.submissionId !== data.submissionId) throw new Error('Die Bestätigung der Datenübermittlung passt nicht zu dieser Voranmeldung.');
      } catch (error) {
        if (error?.name === 'AbortError') throw new Error('Die Übermittlung hat zu lange gedauert. Bitte versuche es noch einmal.');
        throw error;
      } finally {
        window.clearTimeout(timeout);
      }
    }

    setupMessage(internal, customer) {
      return this.config.submission?.showInternalNotices === false ? customer : internal;
    }

    setSubmittingLabel(label) {
      this.next.disabled = Boolean(label);
      if (this.back) this.back.disabled = Boolean(label);
      if (label) this.next.textContent = label;
      else this.updateNavigation();
    }

    showCompletion() {
      this.form.hidden = true;
      this.root.querySelector('.d2-progress')?.setAttribute('hidden', '');
      this.complete.hidden = false;
      this.setVideoSlot('complete', 'complete_minor', this.complete);
      const copy = this.root.querySelector('[data-d2-complete-copy]');
      if (copy) copy.textContent = 'Deine Voranmeldung wurde erfolgreich übermittelt. Komm bitte gemeinsam mit deiner gesetzlichen Vertretung zur Graf-Adolf-Straße 1 in 44534 Lünen. Dort schließen wir den Ausbildungsvertrag ab und klären die Zahlung.';
      this.autoplayVideo(this.complete);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    setStepVideo(step) {
      if (step === 1) this.setVideoSlot('main', 'step_1', this.steps[0]);
      if (step === 2) this.setVideoSlot('main', `selection_${this.selectedCategory() || 'pkw'}`, this.steps[1]);
      if (step === 3) this.setVideoSlot('main', `detail_${this.currentRouteKey()}`, this.steps[2]);
      if (step === 4) this.setVideoSlot('main', this.currentRoute()?.bf17 || this.isMinor() ? 'price_minor' : 'price_adult', this.steps[3]);
      if (step === 5) this.setVideoSlot('main', 'checklist', this.steps[4]);
      if (step === 6) this.setVideoSlot('main', 'personal', this.steps[5]);
      if (step === 7) this.setVideoSlot('main', 'summary', this.steps[6]);
      if (step === 8) this.setVideoSlot('main', this.isMinor() ? 'final_minor' : 'final_adult', this.steps[7]);
    }

    hydrateVideoSlots() {
      this.root.querySelectorAll('[data-d2-video-slot][data-video-key]').forEach((slot) => this.fillVideoSlot(slot, slot.dataset.videoKey));
    }

    setVideoSlot(name, key, scope = this.root) {
      const slot = scope?.querySelector(`[data-d2-video-slot="${name}"]`);
      this.fillVideoSlot(slot, key);
    }

    fillVideoSlot(slot, key) {
      if (!slot || !key || slot.dataset.currentVideo === key) return;
      const template = [...this.root.querySelectorAll('[data-d2-video-template]')].find((item) => item.dataset.d2VideoTemplate === key);
      slot.replaceChildren();
      if (template) slot.append(template.content.cloneNode(true));
      slot.dataset.currentVideo = key;
      slot.querySelector('[data-d2-video-play]')?.addEventListener('click', (event) => this.playVideo(event.currentTarget.closest('[data-d2-video-shell]')));
    }

    activateVideo(step) {
      const shell = [...step.querySelectorAll('[data-d2-video-shell]')].find((item) => !item.closest('[hidden]'));
      if (!shell || shell.dataset.hasVideo !== 'true') return null;
      const video = shell.querySelector('[data-d2-video]');
      if (!video) return null;
      if (video.dataset.loaded !== 'true') {
        video.querySelectorAll('source[data-src]').forEach((source) => { source.src = source.dataset.src; });
        video.dataset.loaded = 'true';
        video.load();
      }
      return shell;
    }

    autoplayVideo(step) {
      const shell = this.activateVideo(step);
      if (shell) this.playVideo(shell);
    }

    playVideo(shell) {
      if (!shell || shell.dataset.hasVideo !== 'true') return;
      const video = shell.querySelector('[data-d2-video]');
      if (!video) return;
      if (video.dataset.loaded !== 'true') {
        video.querySelectorAll('source[data-src]').forEach((source) => { source.src = source.dataset.src; });
        video.dataset.loaded = 'true';
        video.load();
      }
      shell.classList.add('is-playing');
      video.controls = true;
      video.muted = false;
      const playAttempt = video.play();
      if (!playAttempt?.catch) return;
      playAttempt.catch(() => {
        video.muted = true;
        video.play()?.catch(() => {
          video.muted = false;
          video.controls = false;
          shell.classList.remove('is-playing');
        });
      });
    }

    pauseVideos() {
      this.root.querySelectorAll('[data-d2-video]').forEach((video) => {
        if (!video.paused) video.pause();
        try { video.currentTime = 0; } catch (_) {}
        video.controls = false;
        video.closest('[data-d2-video-shell]')?.classList.remove('is-playing');
      });
    }

    field(name) { return this.root.querySelector(`[data-d2-field="${name}"]`); }
    value(name) { return String(this.field(name)?.value || '').trim(); }
    formatDate(value) { if (!value) return '–'; const [year, month, day] = value.split('-'); return `${day}.${month}.${year}`; }
    statusNode(step) { return this.root.querySelector(`[data-d2-status="${step}"]`); }
    showStatus(step, message) { const node = this.statusNode(step); if (node) { node.textContent = message; node.hidden = false; } }
    clearStatuses() { this.root.querySelectorAll('[data-d2-status]').forEach((node) => { node.hidden = true; node.textContent = ''; }); }

    createSubmissionId() {
      if (window.crypto?.randomUUID) return window.crypto.randomUUID();
      return `d2-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }

    restoreSubmissionId() {
      const now = Date.now();
      // Einmalige Bereinigung der bisherigen ID, die nach einem Checkout
      // versehentlich bis zu zwölf Stunden wiederverwendet werden konnte.
      try { window.sessionStorage.removeItem(LEGACY_ID_STORAGE); } catch (_) {}
      try {
        const stored = JSON.parse(window.sessionStorage.getItem(ID_STORAGE) || 'null');
        if (stored?.id && now - Number(stored.createdAt || 0) < ID_TTL) return String(stored.id);
      } catch (_) {}
      const id = this.createSubmissionId();
      try { window.sessionStorage.setItem(ID_STORAGE, JSON.stringify({ id, createdAt: now })); } catch (_) {}
      return id;
    }

    clearSubmissionId() {
      try {
        window.sessionStorage.removeItem(ID_STORAGE);
        window.sessionStorage.removeItem(LEGACY_ID_STORAGE);
      } catch (_) {}
    }

    resetSubmissionId() {
      this.clearSubmissionId();
      this.submissionId = this.restoreSubmissionId();
    }
  }

  const init = () => {
    document.querySelectorAll(ROOT).forEach((root) => {
      if (root.dataset.initialized === 'true') return;
      root.dataset.initialized = 'true';
      new Die2Voranmeldung(root);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.addEventListener('shopify:section:load', init);
})();
